import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PasskeySetupModal from './PasskeySetupModal';
import usePasskey from '../../hooks/usePasskey';
import {
    clearPasskeyOfferAfterLogin,
    getDeviceLabel,
    getPasskeyApiError,
    hasPasskeyOfferAfterLogin,
    isPasskeyCancelled,
    isPasskeySupported,
    listPasskeys,
    pickPasskeyToken,
    pickPasskeyUserId,
} from '../../services/passkeyService';
import { showPmrSuccess } from '../../utils/swalUtils';
import { onNotificationPromptSettled } from '../../utils/notificationPromptGate';

const PasskeyPostLoginPrompt = () => {
    const login = useSelector((state) => state.apiService.login);
    const reduxToken = useSelector((state) => state.apiService.token);
    const [show, setShow] = useState(false);
    const [error, setError] = useState('');
    const {
        supported,
        supportMessage,
        busy,
        register,
    } = usePasskey();

    const token = pickPasskeyToken(login) || (typeof reduxToken === 'string' ? reduxToken.trim() : '');
    const userId = pickPasskeyUserId(login);
    const userName = login?.user_name || login?.email_address || login?.UserName || '';

    useEffect(() => {
        if (!hasPasskeyOfferAfterLogin()) return undefined;
        if (!token || !userId || !isPasskeySupported()) return undefined;

        let cancelled = false;
        let notificationFinished = false;
        let passkeyOfferReady = false;

        const maybeShow = () => {
            if (cancelled || !notificationFinished || !passkeyOfferReady) return;
            setShow(true);
        };

        const stopWaiting = onNotificationPromptSettled(() => {
            notificationFinished = true;
            maybeShow();
        });

        (async () => {
            try {
                const rows = await listPasskeys({
                    token,
                    userId,
                    userName,
                    emailAddress: userName,
                });
                if (cancelled) return;
                if (Array.isArray(rows) && rows.length > 0) {
                    clearPasskeyOfferAfterLogin();
                    return;
                }
                if (Array.isArray(rows) && rows.length === 0) {
                    passkeyOfferReady = true;
                    maybeShow();
                    return;
                }
                clearPasskeyOfferAfterLogin();
            } catch {
                // Never offer create when passkey list cannot be confirmed.
                clearPasskeyOfferAfterLogin();
            }
        })();

        return () => {
            cancelled = true;
            stopWaiting();
        };
    }, [token, userId, userName]);

    const handleSkip = () => {
        if (busy) return;
        clearPasskeyOfferAfterLogin();
        setShow(false);
        setError('');
    };

    const handleSetup = async () => {
        if (!token || !userId) {
            setError('Please sign in again, then try creating a passkey.');
            return;
        }
        try {
            await register({
                userName,
                emailAddress: userName,
                userId,
                token,
                deviceName: getDeviceLabel(),
            });
            clearPasskeyOfferAfterLogin();
            setShow(false);
            setError('');
            showPmrSuccess({
                title: 'Passkey created!',
                message: 'You can now sign in with your fingerprint, face, or screen lock.',
                timer: 2500,
            });
        } catch (err) {
            if (isPasskeyCancelled(err)) return;
            setError(getPasskeyApiError(err, 'Passkey could not be added. You can continue without it, or try again.'));
        }
    };

    return (
        <PasskeySetupModal
            show={show}
            onSkip={handleSkip}
            onSetup={handleSetup}
            busy={busy}
            error={error}
            supported={supported}
            supportMessage={supportMessage}
            title="Create a passkey?"
            description="Use your fingerprint, face, or screen lock to sign in next time — no password needed."
            confirmText="Create passkey"
            cancelText="Not now"
            cancelDisabled={false}
        />
    );
};

export default PasskeyPostLoginPrompt;
